import AccommodationIndividualSelectRoomTile from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomTile'
import AccommodationIndividualSelectRoomTileDetails from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { useCallback, useMemo } from 'react'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { rem } from 'polished'
import AccommodationIndividualSelectRoomOptions from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptions'
import IndividualRoomSelectRoomOption from './IndividualRoomSelectRoomOption'
import useToggle from 'hooks/useToggle'
import type { RemainingAvailabilityByPackage } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { getRemainingAvailabilityByPackage } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { useAirportTransferPackageOptions, useAirportTransferPackageSelection } from 'components/OfferPage/FlashHotel/SameRoom/useAirportTransferPackageOptions'
import type { AirportTransferPackageOptionView } from 'lib/offer/airportTransferPackageGrouping'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

function getHasPackageAvailability(
  packageView: App.OfferPackageOptionView,
  currentlySelectedOption: App.OfferPackageOptionView | undefined,
  availabilities: RemainingAvailabilityByPackage,
) {
  if (packageView.package.uniqueKey === currentlySelectedOption?.package.uniqueKey) {
    return true
  }

  return availabilities.pkgOptionAvailableRooms[packageView.package.uniqueKey] > 0 &&
    availabilities.roomTypeAvailableRooms[packageView.package.roomType.id] > 0
}

interface Props {
  optionVariant: 'stacked' | 'carousel'
  currentRoom: OfferPageRoom
  packageViewOptions: Array<App.OfferPackageOptionView>
  onSelect: (packageOption: App.HotelPackage) => void
  suggestedPackageOption?: {
    option: App.OfferPackageOptionView
    message: string
  }
  availabilities: RemainingAvailabilityByPackage
}

function IndividualRoomSelectRoom({
  optionVariant,
  currentRoom,
  packageViewOptions,
  onSelect,
  suggestedPackageOption,
  availabilities,
}: Props) {
  const currentlySelectedOption = useMemo(() => (
    packageViewOptions.find(option => option.package.id === currentRoom.roomId && option.package.roomRate.id === currentRoom.rateId)
  ), [currentRoom, packageViewOptions])

  const { value: inclusionsOpen, toggle: onInclusionsToggle } = useToggle()

  // Only available/current packages can be collapsed into the airport transfer checkbox.
  // Unavailable cards still need to render separately with their "No rooms left" state.
  const availablePackageViews = useMemo(
    () => packageViewOptions.filter(packageView => getHasPackageAvailability(packageView, currentlySelectedOption, availabilities)),
    [availabilities, currentlySelectedOption, packageViewOptions],
  )
  const groupedAvailablePackageViews = useAirportTransferPackageOptions(availablePackageViews)

  const {
    getPackageSelection,
    onAirportTransferToggle,
  } = useAirportTransferPackageSelection([currentRoom])

  const packageViewsToRender = useMemo<Array<AirportTransferPackageOptionView>>(() => {
    // Merge the grouped available cards back into the original room package order,
    // dropping only transfer packages that were confidently collapsed.
    const groupedPackageViewsByKey = new Map(groupedAvailablePackageViews.map(packageView => [packageView.package.uniqueKey, packageView]))
    const collapsedTransferKeys = new Set(groupedAvailablePackageViews.flatMap(packageView => {
      const transferPackageKey = packageView.airportTransferPackageOption?.transferPackageView.package.uniqueKey
      return transferPackageKey ? [transferPackageKey] : []
    }))

    return packageViewOptions.flatMap(packageView => {
      if (collapsedTransferKeys.has(packageView.package.uniqueKey)) {
        return []
      }

      return groupedPackageViewsByKey.get(packageView.package.uniqueKey) ?? packageView
    })
  }, [groupedAvailablePackageViews, packageViewOptions])

  const carouselWidth = packageViewsToRender.length > 1 ? rem(300) : '100%'

  const renderPackageOption = useCallback((view: AirportTransferPackageOptionView, index: number, variant: 'stacked' | 'carousel') => {
    const airportTransferPackageOption = view.airportTransferPackageOption
    const {
      airportTransferBasePackageKey,
      airportTransferSelected,
      selectedPackageView,
    } = getPackageSelection(view)

    return <IndividualRoomSelectRoomOption
      key={view.package.uniqueKey}
      packageNumber={index + 1}
      packageView={selectedPackageView}
      displayPackageView={airportTransferPackageOption?.basePackageView}
      currentRoom={currentRoom}
      hasAvailability={getHasPackageAvailability(selectedPackageView, currentlySelectedOption, availabilities)}
      onOptionSelect={onSelect}
      variant={variant}
      suggestedPackageMessage={suggestedPackageOption?.option.package.uniqueKey === selectedPackageView.package.uniqueKey ? suggestedPackageOption.message : undefined}
      inclusionsOpen={inclusionsOpen}
      onInclusionsToggle={onInclusionsToggle}
      airportTransferPriceDifference={airportTransferPackageOption?.priceDifference}
      airportTransferInclusion={airportTransferPackageOption?.transferInclusion}
      airportTransferBasePackageKey={airportTransferBasePackageKey}
      airportTransferSelected={airportTransferSelected}
      onAirportTransferToggle={onAirportTransferToggle}
    />
  }, [
    availabilities,
    currentRoom,
    currentlySelectedOption,
    getPackageSelection,
    inclusionsOpen,
    onAirportTransferToggle,
    onInclusionsToggle,
    onSelect,
    suggestedPackageOption?.message,
    suggestedPackageOption?.option.package.uniqueKey,
  ])

  const renderCarouselPackageOption = useCallback(
    (view: AirportTransferPackageOptionView, index: number) => renderPackageOption(view, index, 'carousel'),
    [renderPackageOption],
  )

  const renderStackedPackageOption = useCallback(
    (view: AirportTransferPackageOptionView, index: number) => renderPackageOption(view, index, 'stacked'),
    [renderPackageOption],
  )

  return <AccommodationIndividualSelectRoomTile roomPackage={packageViewOptions[0].package} isCurrentSelection={packageViewOptions[0].package.id === currentRoom.roomId}>
    <AccommodationRoomTileImage
      roomPackage={packageViewOptions[0].package}
      showBookmark={false}
    />
    <AccommodationIndividualSelectRoomTileDetails
      roomPackage={packageViewOptions[0].package}
    />
    <AccommodationIndividualSelectRoomOptions hasOptions>
      {optionVariant === 'carousel' &&
        <CardCarousel width={carouselWidth} gutterSize={20} gap={12} snap="start">
          {packageViewsToRender.map(renderCarouselPackageOption)}
        </CardCarousel>}
      {optionVariant === 'stacked' && packageViewsToRender.map(renderStackedPackageOption)}
    </AccommodationIndividualSelectRoomOptions>
  </AccommodationIndividualSelectRoomTile>
}

type MappedProps = Pick<Props, 'availabilities'>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  availabilities: getRemainingAvailabilityByPackage(state, appState),
}), IndividualRoomSelectRoom)
